/**
 * Phase J-UX — Journal quick-insights row.
 *
 * Renders a compact 4-tile summary above the entry list:
 *   - Total entries
 *   - Private vs shared (split badge)
 *   - Entries this week
 *   - Linked records count (sum of source_event_id + links[])
 *
 * An optional 5th tile shows the current journalling streak — only
 * rendered when the streak is ≥ 2 days, so it's never demotivating.
 *
 * All computation happens client-side from the same `entries`
 * array the page already has — no extra API call.
 */
import React, { useMemo } from "react";
import { BookOpen, Lock, Eye, CalendarRange, Link2, Flame } from "lucide-react";

function startOfThisWeekUTC() {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  const day = d.getUTCDay(); // 0 = Sun
  const offset = day === 0 ? 6 : day - 1;
  d.setUTCDate(d.getUTCDate() - offset);
  return d;
}

function startOfDayLocal(iso) {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d;
}

function computeStreak(entries) {
  if (!entries || entries.length === 0) return 0;
  const dayKeys = new Set();
  for (const e of entries) {
    if (!e.created_at) continue;
    const d = startOfDayLocal(e.created_at);
    dayKeys.add(d.toISOString().slice(0, 10));
  }
  let streak = 0;
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  // Allow the streak to "start" today OR yesterday so a user
  // who hasn't journalled yet today still sees yesterday's chain.
  if (!dayKeys.has(cursor.toISOString().slice(0, 10))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!dayKeys.has(cursor.toISOString().slice(0, 10))) return 0;
  }
  while (dayKeys.has(cursor.toISOString().slice(0, 10))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

function Tile({ icon: Icon, label, value, hint, tone = "neutral", testid }) {
  const tones = {
    neutral: "border-[#D9E1E3] bg-white text-[#2E3A3F]",
    good: "border-[#A7D8DA] bg-[#E0F2F2] text-[#116B6E]",
    warm: "border-[#E6CFA2] bg-[#FBF0DC] text-[#6F4E1E]",
  };
  return (
    <div
      className={`border rounded-md p-3 flex flex-col gap-1 min-w-0 ${tones[tone]}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#6F828A]">
        {Icon ? <Icon className="w-3 h-3" strokeWidth={1.6} /> : null}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-xl font-semibold font-mono-data leading-tight">{value}</div>
      {hint ? <div className="text-[11px] text-[#6F828A] truncate">{hint}</div> : null}
    </div>
  );
}

export function JournalInsights({ entries, userId }) {
  const stats = useMemo(() => {
    const total = entries.length;
    let shared = 0;
    let priv = 0;
    let thisWeek = 0;
    let linked = 0;
    const weekStart = startOfThisWeekUTC();
    for (const e of entries) {
      if (e.is_shared) shared++;
      else if (e.author_id === userId) priv++;
      if (e.created_at) {
        const d = new Date(e.created_at);
        if (d >= weekStart) thisWeek++;
      }
      const linkCount = (e.links || []).length + (e.source_event_id ? 1 : 0);
      if (linkCount > 0) linked++;
    }
    const streak = computeStreak(entries);
    return { total, shared, priv, thisWeek, linked, streak };
  }, [entries, userId]);

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3"
      data-testid="journal-insights"
    >
      <Tile
        icon={BookOpen}
        label="Total entries"
        value={stats.total}
        hint={stats.total === 1 ? "1 entry on record" : `${stats.total} entries on record`}
        testid="journal-insight-total"
      />
      <Tile
        icon={Eye}
        label="Shared / private"
        value={`${stats.shared} / ${stats.priv}`}
        hint={
          stats.shared + stats.priv === 0
            ? "No entries yet"
            : `${stats.shared} visible to co-parent`
        }
        tone="good"
        testid="journal-insight-visibility"
      />
      <Tile
        icon={CalendarRange}
        label="This week"
        value={stats.thisWeek}
        hint={stats.thisWeek > 0 ? "Entries this week" : "No entries yet this week"}
        testid="journal-insight-week"
      />
      <Tile
        icon={Link2}
        label="Linked records"
        value={stats.linked}
        hint={
          stats.linked > 0
            ? `${stats.linked} ${stats.linked === 1 ? "entry" : "entries"} linked`
            : "Nothing linked yet"
        }
        testid="journal-insight-linked"
      />
      {stats.streak >= 2 ? (
        <Tile
          icon={Flame}
          label="Current streak"
          value={`${stats.streak}d`}
          hint="Days in a row"
          tone="warm"
          testid="journal-insight-streak"
        />
      ) : null}
    </div>
  );
}

export default JournalInsights;
